import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useMessageStore } from '../store';
import { useAuthStore } from '../store/authStore';
import MessageCard from '../components/MessageCard';
import Button from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { RiSearchLine, RiCloseLine, RiBellLine, RiUserLine, RiMailLine, RiMailOpenLine, RiArrowLeftLine, RiArrowRightLine, RiReplyLine, RiReplyAllLine, RiShareForwardLine, RiAttachmentLine, RiCalendarLine } from '@remixicon/react';
import '@/__styles/MessageList.css';

/**
 * MessageList Page
 * Displays all messages with filtering, search, and pagination
 * Uses Zustand for state management
 */
const MessageList = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  // Zustand stores
  const { userProfile } = useAuthStore();
  const {
    messages,
    selectedMessage,
    activeFilter,
    searchQuery,
    loading,
    error,
    pagination,
    setActiveFilter,
    setSearchQuery,
    loadMessages,
    viewMessage,
    nextPage,
    prevPage,
    getUnreadCount
  } = useMessageStore();

  /**
   * Check if user is authenticated
   */
  const checkAuth = useCallback(async () => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        navigate('/signin');
        return;
      }

      setUser(session.user);
    } catch (err) {
      console.error('Auth check error:', err);
      navigate('/signin');
    }
  }, [navigate]);

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Load messages when authenticated
  useEffect(() => {
    if (user && userProfile) {
      loadMessages(userProfile);
    }
  }, [user, userProfile, loadMessages]);

  // Reload messages when pagination or filters change
  useEffect(() => {
    if (user && userProfile) {
      loadMessages(userProfile);
    }
  }, [pagination.currentPage, activeFilter, searchQuery, userProfile]);

  /**
   * Handle filter change
   */
  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  /**
   * Handle search input
   */
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Handle notification bell click
   */
  const handleNotificationClick = () => {
    console.log('Notifications clicked');
  };

  /**
   * Handle profile click
   */
  const handleProfileClick = () => {
    navigate('/profile-setup');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-4">
        <div className="flex items-center justify-between max-w-8xl mx-auto">
          <div className="flex items-center space-x-3">
            <div className="h-8 w-8 bg-primary-100 rounded-lg flex items-center justify-center">
              <RiMailLine className="h-5 w-5 text-primary-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleNotificationClick}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Notifications"
            >
              <RiBellLine className="h-5 w-5" />
            </button>
            <button
              onClick={handleProfileClick}
              className="flex items-center space-x-2 p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Profile"
            >
              <div className="h-8 w-8 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
                {user?.user_metadata?.full_name?.charAt(0) || '?'}
              </div>
              <span className="text-green-500 text-xs">✓</span>
            </button>
          </div>
        </div>
      </header>

      {/* Search Bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-8xl mx-auto">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <RiSearchLine className="h-5 w-5 text-gray-400" />
            </div>
            <Input
              type="text"
              placeholder="Search by sender, recipient, or subject..."
              value={searchQuery}
              onChange={handleSearch}
              className="pl-10 pr-10 h-12 w-full bg-gray-50 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                <RiCloseLine className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="bg-white border-b border-gray-200 px-4">
        <div className="max-w-8xl mx-auto">
          <div className="flex space-x-1 overflow-x-auto">
            {[
              { key: 'all', label: 'All' },
              { key: 'unread', label: 'Unread', count: getUnreadCount() },
              { key: 'read', label: 'Read' },
              { key: 'inbound', label: 'Inbound' },
              { key: 'outbound', label: 'Outbound' }
            ].map((filter) => (
              <button
                key={filter.key}
                onClick={() => handleFilterChange(filter.key)}
                className={`relative px-4 py-3 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                  activeFilter === filter.key
                    ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                {filter.label}
                {filter.count > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
                    {filter.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content - Two Panel Layout */}
      <main className="flex-1 flex bg-white shadow-sm rounded-lg m-4 overflow-hidden">
        {/* Left Panel - Message List */}
        <div className="w-1/3 border-r border-gray-200 flex flex-col">
          {loading ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading messages...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <div className="text-red-500 mb-4">
                  <RiMailLine className="h-12 w-12 mx-auto" />
                </div>
                <p className="text-red-600 mb-4">{error}</p>
                <Button onClick={() => loadMessages(userProfile)} className="bg-red-500 hover:bg-red-600 text-white">
                  Retry
                </Button>
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <div className="text-gray-400 mb-4">
                  <RiMailLine className="h-16 w-16 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No messages found</h3>
                <p className="text-gray-500">
                  {searchQuery
                    ? `No messages match "${searchQuery}"`
                    : activeFilter === 'all'
                    ? 'Your inbox is empty.'
                    : `No ${activeFilter} messages at the moment.`}
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto">
                <div className="divide-y divide-gray-200">
                  {messages.map((message) => (
                    <MessageCard
                      key={message.message_id}
                      message={message}
                      onViewMessage={viewMessage}
                    />
                  ))}
                </div>
              </div>

              {/* Pagination Controls */}
              <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between">
                <Button
                  onClick={prevPage}
                  disabled={pagination.currentPage === 0}
                  variant="outline"
                  className="flex items-center space-x-2 disabled:opacity-50"
                >
                  <RiArrowLeftLine className="h-4 w-4" />
                  <span>Previous</span>
                </Button>
                <span className="text-sm text-gray-700">
                  Page {pagination.currentPage + 1}
                </span>
                <Button
                  onClick={nextPage}
                  disabled={messages.length < pagination.limit}
                  variant="outline"
                  className="flex items-center space-x-2 disabled:opacity-50"
                >
                  <span>Next</span>
                  <RiArrowRightLine className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Right Panel - Message Content */}
        <div className="flex-1 flex flex-col min-w-0">
          {selectedMessage ? (
            <div className="flex-1 flex flex-col">
              {/* Message Header */}
              <div className="border-b border-gray-200 p-6">
                <div className="flex items-start space-x-4">
                  <div className="h-12 w-12 bg-primary-500 text-white rounded-full flex items-center justify-center text-lg font-medium flex-shrink-0">
                    {selectedMessage.from_name?.charAt(0) || selectedMessage.from_email?.charAt(0) || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">
                      {selectedMessage.from_name || selectedMessage.from_email}
                    </h3>
                    <p className="text-sm text-gray-500 truncate">{selectedMessage.from_email}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {!selectedMessage.is_read && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        New
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Message Subject */}
                <div className="mt-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {selectedMessage.subject || '(No Subject)'}
                  </h2>
                </div>
              </div>

              {/* Message Metadata */}
              <div className="border-b border-gray-200 px-6 py-4 bg-gray-50">
                <div className="space-y-3 text-sm">
                  <div className="flex items-start">
                    <span className="font-medium text-gray-700 w-16 flex-shrink-0">From:</span>
                    <span className="text-gray-900">
                      {selectedMessage.from_name ? `${selectedMessage.from_name} <${selectedMessage.from_email}>` : selectedMessage.from_email}
                    </span>
                  </div>
                  <div className="flex items-start">
                    <span className="font-medium text-gray-700 w-16 flex-shrink-0">To:</span>
                    <span className="text-gray-900">{selectedMessage.to_email}</span>
                  </div>
                  {selectedMessage.cc_email && (
                    <div className="flex items-start">
                      <span className="font-medium text-gray-700 w-16 flex-shrink-0">CC:</span>
                      <span className="text-gray-900">{selectedMessage.cc_email}</span>
                    </div>
                  )}
                  {selectedMessage.bcc_email && (
                    <div className="flex items-start">
                      <span className="font-medium text-gray-700 w-16 flex-shrink-0">BCC:</span>
                      <span className="text-gray-900">{selectedMessage.bcc_email}</span>
                    </div>
                  )}
                  <div className="flex items-center">
                    <RiCalendarLine className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-gray-600">{formatDate(selectedMessage.received_at)}</span>
                  </div>
                  {selectedMessage.has_attachments && (
                    <div className="flex items-center">
                      <RiAttachmentLine className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-gray-600">Has attachments</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Message Body */}
              <div className="flex-1 overflow-auto p-6">
                {selectedMessage.html_body ? (
                  <div
                    className="email-content prose prose-sm sm:prose max-w-none text-gray-900"
                    style={{
                      overflowX: 'auto',
                      wordBreak: 'break-word'
                    }}
                    dangerouslySetInnerHTML={{ __html: selectedMessage.html_body }}
                  />
                ) : selectedMessage.text_body ? (
                  <div className="whitespace-pre-wrap text-gray-900 font-mono text-sm leading-relaxed">
                    {selectedMessage.text_body}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <RiMailLine className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No content available</p>
                  </div>
                )}
              </div>

              {/* Message Actions */}
              <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
                <div className="flex space-x-3">
                  <Button className="flex items-center space-x-2 bg-primary-500 hover:bg-primary-600 text-white">
                    <RiReplyLine className="h-4 w-4" />
                    <span>Reply</span>
                  </Button>
                  <Button variant="outline" className="flex items-center space-x-2">
                    <RiReplyAllLine className="h-4 w-4" />
                    <span>Reply All</span>
                  </Button>
                  <Button variant="outline" className="flex items-center space-x-2">
                    <RiShareForwardLine className="h-4 w-4" />
                    <span>Forward</span>
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <div className="text-gray-400 mb-4">
                  <RiMailOpenLine className="h-16 w-16 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No message selected</h3>
                <p className="text-gray-500">Select a message from the list to view its content</p>
              </div>
            </div>
          )}
        </div>
      </main>

    </div>
  );
};

export default MessageList;
